import PropTypes from 'prop-types';
import { useRef, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';

import { getContent, isDDEnv } from '@/utils/helper';
import { analytics, getClickHeaderNavigation } from '@/utils/analytics';

import ClipAnimation from '@/atoms/ClipAnimation/ClipAnimation';
import Icon from '@/atoms/Icon/Icon';
import Text from '@/atoms/Text/Text';
import BlockText from '@/molecules/BlockText/BlockText';
import { CtaWrapper, Cta } from '@/molecules/CTA/index';
import { DynamicAsset } from '@/molecules/Media/index';
// import SearchInputText from '@/molecules/Search/SearchInputText';

import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';
import { CtfCtaStyle } from '@/utils/constants';

function Navigation({
  open = false,
  closeNavigation = () => {},
  fields,
  entries,
  assets,
  slug = 'home',
  socials,
}) {
  const { asPath, push } = useRouter();

  const exploreDivRef = useRef(null);
  const [canStartAnimation, setcanStartAnimation] = useState(false);
  const [activeNavigation, setActiveNavigation] = useState(slug);
  // const [search, setSearch] = useState('');

  const hideExplore = () => {
    closeNavigation();
  };
  // const handleSearchChange = (e) => setSearch(e?.target?.value);
  // const handleSearchSubmit = () => {
  //   push(`/search?q=${search}&fromSearch=true`, `/search?q=${search}`);
  //   hideExplore();
  // };

  //on open, run clip animation and set input focus
  useEffect(() => {
    const exploreDiv = exploreDivRef.current;

    const handleTransitionEnd = ({ target: { classList }, propertyName }) => {
      const navigationOpen =
        classList.contains('aaco-navigation') && propertyName === 'opacity';
      if (navigationOpen) {
        setcanStartAnimation(open);
      }
    };

    exploreDiv.addEventListener('transitionend', handleTransitionEnd);

    return () =>
      exploreDiv.removeEventListener('transitionend', handleTransitionEnd);
  }, [open]);

  const navigationMenu =
    fields?.length > 0
      ? fields.map((link) => {
          const menuContent = getContent(link.sys.id, entries)?.fields;

          // get page preview & url per link
          const page =
            menuContent?.pages && menuContent?.pages.length
              ? getContent(menuContent?.pages[0].sys.id, entries)
              : menuContent?.page
              ? getContent(menuContent?.page?.sys?.id, entries)
              : null;
          return {
            ...menuContent,
            pageUrl: page?.fields?.slug,
            pageContent: {
              cloudinaryPreviewAssets: page?.fields?.cloudinaryPreviewAssets,
              previewAsset: page?.fields?.previewAsset,
              previewText: getContent(
                page?.fields?.previewText?.sys?.id,
                entries
              ),
            },
          };
        })
      : [];

  const socialLinks =
    socials?.length > 0
      ? socials.map((social) => getContent(social.sys.id, entries))
      : [];
  const isDD = isDDEnv();

  const handleMenuClick = (e, url) => {
    analytics({
      window,
      document,
      detail: getClickHeaderNavigation(e, { url }),
    });
    closeNavigation();
  };
  return (
    <div
      ref={exploreDivRef}
      className={cn(
        'aaco-navigation',
        'absolute hidden-print top-0 z-[100] left-0 w-full min-h-screen px-7 lg:h-[100vh] lg:overflow-y-auto',
        'bg-light-warm-grey text-white',
        'transition-all duration-300',
        {
          'visible opacity-100': open,
          'invisible opacity-0': !open,
        }
      )}
    >
      <div
        className={cn(
          'lg:flex lg:flex-col',
          'lg:container lg:mx-auto min-h-full py-8 md:px-0'
        )}
      >
        <div className="aaco-navigation__close flex justify-end items-center">
          <div className="cursor-pointer" onClick={hideExplore}>
            <Icon name="cross" className="w-6 md:w-4 h-6 md:h-4 text-black" />
          </div>
        </div>

        <div className="aaco-navigation__page flex flex-col lg:flex-row lg:justify-between lg:items-start lg:grow">
          {/* START: navigation left */}
          <div
            className={cn(
              'aaco-navigation__links',
              'flex flex-col justify-between lg:self-stretch',
              'w-full lg:w-1/2 lg:min-h-full',
              ' mb-5 md:mb-10 md:mt-11'
            )}
          >
            <ul>
              {navigationMenu.length > 0 &&
                navigationMenu.map((m) => {
                  if (!m?.name) return;

                  const isCurrentPage = m.pageUrl === asPath?.replace('/', '');
                  const isActive = m.pageUrl === activeNavigation;
                  return (
                    <li
                      key={m.name}
                      className={cn(
                        'aaco-navigation__link',
                        'pb-2.5 md:pb-4 last:pb-0',
                        {
                          'aaco-navigation__link--active':
                            isActive || isCurrentPage,
                        }
                      )}
                      onMouseEnter={() => setActiveNavigation(m.pageUrl)}
                    >
                      <ClipAnimation startAnimation={canStartAnimation}>
                        <CtaWrapper
                          url={m?.url}
                          page={m?.pageUrl}
                          onClick={(e) =>
                            handleMenuClick(e, m?.url ?? `/${m?.pageUrl}`)
                          }
                        >
                          <Text
                            tag="h3"
                            style={cn(
                              'font-heading text-h3-mobile md:text-h3 md:leading-none',
                              {
                                'text-black': isActive || isCurrentPage,
                                'text-eucalyptus': !(isActive || isCurrentPage),
                              }
                            )}
                            custom
                          >
                            {m.name}
                          </Text>
                        </CtaWrapper>
                      </ClipAnimation>
                    </li>
                  );
                })}
            </ul>
            {/* START: search mobile */}
            {/* <div className="aaco-navigation__search w-full mt-6 relative lg:hidden">
              <SearchInputText
                value={search}
                onChange={handleSearchChange}
                onSearch={handleSearchSubmit}
              />
            </div> */}
            {/* END: search mobile */}

            {/* START: Social */}
            <div className="aaco-navigation__social-links md:col-span-2 w-full mb-10 md:mb-0 mt-6 lg:mt-2.5">
              <div className="aaco-navigation__portal-link dd-en:hidden dd-kr:hidden mb-2.5">
                <Cta
                  buttonProps={{
                    invert: isDD,
                    name: 'PORTAL',
                    type: CtfCtaStyle.SECONDARY,
                    style: 'w-fit text-acacia',
                  }}
                  iconProps={{
                    hasIconBorder: false,
                  }}
                  linkProps={{
                    url: process.env.NEXT_PUBLIC_PORTAL_LINK,
                  }}
                />
              </div>

              {socialLinks.length > 0 && (
                <ul className="mb-0 p-0 flex items-center">
                  {socialLinks.map((link, index) => (
                    <li
                      key={index}
                      className="aaco-navigation__social-link block mr-5 md:mr-3"
                    >
                      <a
                        href={link.fields?.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={link.fields?.name}
                        className="flex flex-col w-10 md:w-8 h-10 md:h-8 p-3 md:p-2.5 justify-center items-center rounded-full bg-acacia"
                      >
                        <Icon
                          name={link.fields?.icon}
                          className="block text-warm-grey"
                        />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* END: Social */}
          </div>
          {/* END: navigation left */}

          {/* START: navigation right */}
          <div
            className={`aaco-navigation__preview flex flex-col min-h-full lg:h-[100%] lg:w-1/2 lg:ml-6 lg:mt-10 ${
              open ? 'block' : 'hidden'
            }`}
          >
            {navigationMenu.length > 0 &&
              navigationMenu.map((m) => {
                const isActive = m.pageUrl === activeNavigation;
                if (
                  m?.pageContent?.previewAsset ||
                  m.pageContent?.cloudinaryPreviewAssets
                ) {
                  return (
                    <div
                      className={`aaco-navigation__preview__image h-[20vh] lg:h-[60%] ${
                        isActive ? 'visible' : 'hidden'
                      }`}
                      key={m.name}
                    >
                      <DynamicAsset
                        assets={assets}
                        entries={entries}
                        field={
                          m?.pageContent?.cloudinaryPreviewAssets ??
                          m?.pageContent?.previewAsset
                        }
                        container="container"
                        style="object-top"
                      />
                    </div>
                  );
                }
              })}
            <div className="aaco-navigation__preview__text">
              {navigationMenu.length > 0 &&
                navigationMenu.map((m, i) => {
                  const isActive = m.pageUrl === activeNavigation;
                  if (!isActive) {
                    return;
                  }
                  return (
                    <BlockText
                      blockTextStyle="gap-y-4"
                      blockWrapperStyle="py-4 px-0 md:px-0"
                      fields={m.pageContent?.previewText?.fields}
                      richTextStyle={{
                        paragraph: {
                          blockquoteFont: TEXT_BASE_CLASS['h6'],
                          customFont: TEXT_BASE_CLASS['body'],
                        },
                      }}
                      invert
                      alignBlockQuoteLeft={true}
                      key={i}
                    />
                  );
                })}
            </div>
          </div>
          {/* END: navigation right */}
        </div>
      </div>
    </div>
  );
}

Navigation.propTypes = {
  open: PropTypes.bool,
  closeNavigation: PropTypes.func,
  slug: PropTypes.string,
};


export default Navigation;
